import {
  handleSaveDraftActivity,
  handleCreateActivity,
  handleUpdateDraftActivity,
  handleUpdateActivity,
  validateActivityName,
  handleGetActivity
} from '@/services/activities';
import {useAppDispatch, useAppSelector} from '@/store';
import {
  resetActivityData,
  setActivityFormData
} from '@/store/slices/activitySlice';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {
  ACTIVITY_TYPE,
  ACTIVITY_TYPES_ENUM,
  isVideoActivityType,
  isFreeKickActivityType,
  isWheelOfFortuneActivityType,
  isBackgammonActivityType
} from '@/types/Activity';
import {
  getDateFromISOString,
  getUUID,
  formatUTCTimestamp,
  isPublished,
  handleNameValidationViaCallbackFn,
  isInProgress,
  getISOTimeStringForReview,
  getCurrencySymbol,
  extractTimeFromISODateTime
} from '@/utils/commons';
import {ROUTES} from '@/utils/constants/routesConstants';
import {ACTIVITY_STATUS} from '@/utils/constants/activityConstants';
import {useCallback, useState} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {COUNTRY_CODES_FREQUENCY} from '@/utils/constants/globalConstants';
import {useActivityValidation} from '@/hooks/useActivityValidation';
import {
  buildIncludedSegmentGroups,
  segmentIdsByName,
  segmentNameOf
} from '@/helpers/rewardsUtils';
import {
  normalizeActivitySegmentInput,
  omitDeprecatedSegmentWireKeys,
  resolveDisplayOrderFromApi,
  resolveRawSegmentExcludeFromForm,
  resolveSegmentExcludeFromApi
} from '@/utils/activityReviewUtils';
import {
  buildQuizQuestionsList,
  buildWheelOfFortuneDraft,
  buildWofOptionalFields,
  coerceNumericFields,
  extractDisplayConfigFields,
  excludedFieldsForReviewOrder,
  hasAnyNonEmptyValue,
  mapApiExcludedSegmentsToForm,
  normalizeSegmentsFromApi,
  buildPreprocessedResult
} from './activityReviewUtils';
import {PERIOD_MAP, periodMap} from '@/utils/constants/commonConstants';

const typeOfActivity = ['Welcome Activity', 'Login', 'Easy Order'];

const ACTIVITY_TYPE_TO_ENUM = {
  [ACTIVITY_TYPE.WELCOME_ACTIVITY]: ACTIVITY_TYPES_ENUM.WELCOME,
  [ACTIVITY_TYPE.LOGIN]: ACTIVITY_TYPES_ENUM.LOGIN,
  [ACTIVITY_TYPE.GAME_FREE_KICK]: ACTIVITY_TYPES_ENUM.GAME_FREE_KICK,
  [ACTIVITY_TYPE.SURVEY]: ACTIVITY_TYPES_ENUM.SURVEY,
  [ACTIVITY_TYPE.POINTS_TRANSFER]: ACTIVITY_TYPES_ENUM.POINTS_TRANSFER,
  [ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY]:
    ACTIVITY_TYPES_ENUM.POINTS_ON_ORDER_DELIVERY,
  [ACTIVITY_TYPE.REDEEM_POINTS]: ACTIVITY_TYPES_ENUM.REDEEM_POINTS,
  [ACTIVITY_TYPE.GENERIC_ACTIVITY]: ACTIVITY_TYPES_ENUM.GENERIC_ACTIVITY,
  [ACTIVITY_TYPE.POINT_EXPIRATION]: ACTIVITY_TYPES_ENUM.POINT_EXPIRATION,
  [ACTIVITY_TYPE.PAY_BY_POINTS]: ACTIVITY_TYPES_ENUM.PAY_BY_POINTS,
  [ACTIVITY_TYPE.CURRENCY_TO_POINTS]: ACTIVITY_TYPES_ENUM.CURRENCY_TO_POINTS,
  [ACTIVITY_TYPE.GAME_WHEEL_OF_FORTUNE]:
    ACTIVITY_TYPES_ENUM.GAME_WHEEL_OF_FORTUNE,
  [ACTIVITY_TYPE.GAME_BACKGAMMON]: ACTIVITY_TYPES_ENUM.GAME_BACKGAMMON,
  [ACTIVITY_TYPE.EASY_ORDER]: ACTIVITY_TYPES_ENUM.EASY_ORDER
};

const getActivityEnum = (activityType) => {
  if (isVideoActivityType(activityType)) {
    return ACTIVITY_TYPES_ENUM.WATCH_VIDEO;
  }
  return ACTIVITY_TYPE_TO_ENUM[activityType] || null;
};

const getSlug = (activityType, gameSlug) => {
  if (activityType === ACTIVITY_TYPE.POINT_EXPIRATION) {
    return 'reward-points::rolling-points-expiry';
  }
  if (activityType === ACTIVITY_TYPE.PAY_BY_POINTS) {
    return `reward-points::pay-by-points-${Date.now()}`;
  }
  if (isFreeKickActivityType(activityType) && gameSlug) {
    return gameSlug;
  }
  if (isBackgammonActivityType(activityType) && gameSlug) {
    return gameSlug;
  }
  return `reward-points::${activityType.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}`;
};

const resolveTrimmedName = (activityName, defaultPrefix) => {
  const trimmed = activityName && String(activityName).trim();
  return trimmed || `${defaultPrefix}_${formatUTCTimestamp()}`;
};

const requiresNameValidation = (activityType) =>
  activityType === ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY ||
  activityType === ACTIVITY_TYPE.PAY_BY_POINTS ||
  activityType === ACTIVITY_TYPE.SURVEY ||
  isVideoActivityType(activityType) ||
  isFreeKickActivityType(activityType) ||
  isWheelOfFortuneActivityType(activityType) ||
  isBackgammonActivityType(activityType);

const getNameValidationLabel = (activityType) => {
  if (activityType === ACTIVITY_TYPE.SURVEY) return 'Survey Name';
  if (isVideoActivityType(activityType)) return 'Video Name';
  if (isFreeKickActivityType(activityType)) return 'Game Name';
  if (isBackgammonActivityType(activityType)) return 'Game Name';
  return 'Activity Name';
};

const getTags = (activityType) => {
  if (activityType === ACTIVITY_TYPE.PAY_BY_POINTS) return 'paid-by-points';
  if (activityType === ACTIVITY_TYPE.CURRENCY_TO_POINTS)
    return 'currency-to-points';
  return '';
};

const getPointsEarnedForPublish = (formData) => {
  if (formData?.activityType === ACTIVITY_TYPE.PAY_BY_POINTS) {
    return formData?.pointsToRedeem;
  }
  return formData?.pointsEarned;
};

const getFormattedLocalCurrency = (formData, countryCode) => {
  if (
    formData?.activityType === ACTIVITY_TYPE.CURRENCY_TO_POINTS &&
    formData?.localCurrency
  ) {
    return `${formData.localCurrency} ${getCurrencySymbol(countryCode).toLowerCase()}`;
  }
  if (formData?.activityType === ACTIVITY_TYPE.PAY_BY_POINTS) {
    return formData?.localCurrencyValue;
  }
  return formData?.localCurrency;
};

const buildSurveyPublishPayload = (formData) => ({
  ...(formData?.allTimeLimit != null && formData.allTimeLimit !== ''
    ? {allTimeLimit: String(formData.allTimeLimit)}
    : {}),
  displayOrder:
    formData?.displayOrder !== '' &&
    formData?.displayOrder !== undefined &&
    formData?.displayOrder !== null
      ? formData.displayOrder
      : 0,
  ...(Array.isArray(formData?.customerType) && formData.customerType.length > 0
    ? {customerType: formData.customerType}
    : {}),
  ...(formData?.url ? {url: formData.url} : {})
});

/**
 * Transform UI leaderboard structure to API format
 * @param {Object} leaderboards - UI structure: { Daily: {data, fileName, lastUpdate}, ... }
 * @returns {Array} API structure: [{leaderboardPeriod: "DAILY", leaderboardRewardPoints: [...]}]
 */
const buildLeaderboardRewardsList = (leaderboards) => {
  if (!leaderboards || typeof leaderboards !== 'object') {
    return [];
  }

  const result = [];

  Object.keys(PERIOD_MAP).forEach((period) => {
    const periodData = leaderboards[period];
    if (
      periodData &&
      Array.isArray(periodData.data) &&
      periodData.data.length > 0
    ) {
      result.push({
        leaderboardPeriod: PERIOD_MAP[period],
        leaderboardFileName:
          periodData.leaderboardFileName || periodData.fileName || '',
        leaderboardFileUrl: periodData.leaderboardFileUrl || '',
        updatedAt: periodData.updatedAt || periodData.lastUpdate || '',
        leaderboardRewardPoints: periodData.data.map((item) => ({
          rank: item.rank,
          points: item.points
        }))
      });
    }
  });

  return result;
};

/**
 * Transform API leaderboard structure to UI format
 * @param {Array} leaderboardRewardsList - API structure: [{leaderboardPeriod: "DAILY", leaderboardRewardPoints: [...]}]
 * @returns {Object} UI structure: { Daily: {data, fileName, lastUpdate}, ... }
 */
const mapLeaderboardRewardsListToUI = (leaderboardRewardsList) => {
  const leaderboards = {};

  if (
    !Array.isArray(leaderboardRewardsList) ||
    leaderboardRewardsList.length === 0
  ) {
    return leaderboards;
  }

  leaderboardRewardsList.forEach((item) => {
    const uiPeriod = periodMap[item.leaderboardPeriod];
    if (uiPeriod && Array.isArray(item.leaderboardRewardPoints)) {
      leaderboards[uiPeriod] = {
        data: item.leaderboardRewardPoints.map((point) => ({
          rank: point.rank,
          points: point.points
        })),
        leaderboardFileName: item.leaderboardFileName || '',
        leaderboardFileUrl: item.leaderboardFileUrl || '',
        updatedAt: item.updatedAt || '',
        fileName: item.leaderboardFileName || '',
        lastUpdate: item.updatedAt || ''
      };
    }
  });

  return leaderboards;
};

const buildFreeKickGamePublishPayload = (formData) => {
  const hasStartTime =
    formData?.dailyDisplayStartTime &&
    formData.dailyDisplayStartTime.trim() !== '';
  const hasEndTime =
    formData?.dailyDisplayEndTime && formData.dailyDisplayEndTime.trim() !== '';

  const firstLangCode = formData?.translations
    ? Object.keys(formData.translations)[0]
    : null;

  const displayConfig = {
    startTime: hasStartTime
      ? formData.dailyDisplayStartTime
      : hasEndTime
        ? ''
        : '00:00',
    endTime: hasEndTime
      ? formData.dailyDisplayEndTime
      : hasStartTime
        ? ''
        : '23:59',
    daysOfTheWeekUnavailable: formData?.daysOfWeekUnavailable || [],
    dailyLimit:
      formData?.dailyPlayLimit === '' ||
      formData?.dailyPlayLimit === null ||
      formData?.dailyPlayLimit === undefined
        ? 1
        : formData.dailyPlayLimit,
    firstTeamName: firstLangCode
      ? formData.translations[firstLangCode]?.firstTeamName || ''
      : '',
    secondTeamName: firstLangCode
      ? formData.translations[firstLangCode]?.secondTeamName || ''
      : ''
  };

  const existingAsset = formData?.displayConfig?.asset;
  if (hasAnyNonEmptyValue(existingAsset)) {
    displayConfig.asset = existingAsset;
  }

  // Transform leaderboards UI structure to API format
  const leaderboardRewardsList = buildLeaderboardRewardsList(
    formData?.leaderboards
  );

  return {
    displayConfig,
    ...(hasAnyNonEmptyValue(formData?.translations)
      ? {translations: formData.translations}
      : {}),
    ...(leaderboardRewardsList.length > 0 ? {leaderboardRewardsList} : {})
  };
};

// Build the FreeKick draft payload by explicitly picking only the fields the
// API expects from Redux formData. Avoids spreading the whole form state and
// having to strip deprecated/UI-only keys afterwards.
const buildFreeKickDraftPayload = (formData) => {
  const payload = {};

  // Identifying / lifecycle fields
  if (formData?.activityType !== undefined) {
    payload.activityType = formData.activityType;
  }
  if (formData?.activityName !== undefined) {
    payload.activityName = formData.activityName;
  }
  if (formData?.status !== undefined) payload.status = formData.status;
  if (formData?.uuid) payload.uuid = formData.uuid;
  if (formData?.id) payload.id = formData.id;
  if (formData?.slug) payload.slug = formData.slug;
  if (formData?.tags !== undefined) payload.tags = formData.tags;

  // Schedule
  if (formData?.activityStartDate) {
    payload.activityStartDate = formData.activityStartDate;
  }
  if (formData?.activityEndDate) {
    payload.activityEndDate = formData.activityEndDate;
  }
  if (formData?.hasEndDate !== undefined) {
    payload.hasEndDate = formData.hasEndDate;
  }

  // Points
  if (formData?.pointsEarned !== undefined) {
    payload.pointsEarned = formData.pointsEarned;
  }

  // Game-specific payload (displayConfig + translations + leaderboardRewardsList)
  Object.assign(payload, buildFreeKickGamePublishPayload(formData));

  // Segments
  if (formData?.allCustomers !== undefined) {
    payload.allCustomers = formData.allCustomers;
  }
  Object.assign(payload, buildActivitySegmentsPayload(formData));
  Object.assign(payload, buildActivityExcludedPayload(formData));

  return payload;
};

const buildEasyOrderDraftPayload = (formData) => {
  const payload = {};

  if (formData?.activityType !== undefined) {
    payload.activityType = formData.activityType;
  }
  if (formData?.activityName !== undefined) {
    payload.activityName = formData.activityName;
  }
  if (formData?.status !== undefined) payload.status = formData.status;
  if (formData?.uuid) payload.uuid = formData.uuid;
  if (formData?.id) payload.id = formData.id;

  if (formData?.activityStartDate) {
    payload.activityStartDate = formData.activityStartDate;
  }
  if (formData?.activityEndDate !== undefined) {
    payload.activityEndDate = formData.activityEndDate;
  }
  if (formData?.hasEndDate !== undefined) {
    payload.hasEndDate = formData.hasEndDate;
  }

  if (
    formData?.pointsForSuccessfulUpload !== undefined &&
    formData.pointsForSuccessfulUpload !== ''
  ) {
    payload.pointsForSuccessfulUpload = Number(
      formData.pointsForSuccessfulUpload
    );
  }
  if (
    formData?.pointsPerLineItem !== undefined &&
    formData.pointsPerLineItem !== ''
  ) {
    payload.pointsPerLineItem = Number(formData.pointsPerLineItem);
  }
  if (
    formData?.photoOrderModalDisplayLimit !== undefined &&
    formData.photoOrderModalDisplayLimit !== ''
  ) {
    payload.photoOrderModalDisplayLimit = Number(
      formData.photoOrderModalDisplayLimit
    );
  }

  return payload;
};

const buildEasyOrderPublishPayload = (formData) => ({
  pointsForSuccessfulUpload: Number(formData?.pointsForSuccessfulUpload),
  pointsPerLineItem: Number(formData?.pointsPerLineItem),
  photoOrderModalDisplayLimit: Number(formData?.photoOrderModalDisplayLimit)
});

const buildWheelOfFortunePublishPayload = (formData) => {
  const hasStartTime =
    formData?.dailyDisplayStartTime &&
    formData.dailyDisplayStartTime.trim() !== '';
  const hasEndTime =
    formData?.dailyDisplayEndTime && formData.dailyDisplayEndTime.trim() !== '';

  const displayConfig = {
    startTime: hasStartTime
      ? formData.dailyDisplayStartTime
      : hasEndTime
        ? ''
        : '00:00',
    endTime: hasEndTime
      ? formData.dailyDisplayEndTime
      : hasStartTime
        ? ''
        : '23:59',
    ...(Array.isArray(formData?.daysOfWeekUnavailable) &&
      formData.daysOfWeekUnavailable.length > 0 && {
        daysOfTheWeekUnavailable: formData.daysOfWeekUnavailable
      }),
    ...(formData?.dailyPlayLimit != null && {
      dailyLimit: formData.dailyPlayLimit
    }),
    ...(hasAnyNonEmptyValue(formData?.displayConfig?.asset) && {
      asset: formData.displayConfig.asset
    }),
    gameConfig: {
      budget: {
        ...(formData?.budgetPeriod && {
          frequency: formData.budgetPeriod.toUpperCase()
        }),
        ...(formData?.pointsEarned != null && {
          amount: Number.parseInt(formData.pointsEarned, 10) || 0
        })
      },
      ...(formData?.displayConfig?.gameConfig?.equalProbabilityPerSlice !==
        undefined && {
        equalProbabilityPerSlice:
          formData.displayConfig.gameConfig.equalProbabilityPerSlice
      }),
      ...(formData?.displayConfig?.gameConfig?.equalPointsPerSlice !==
        undefined && {
        equalPointsPerSlice:
          formData.displayConfig.gameConfig.equalPointsPerSlice
      }),
      ...(Array.isArray(formData?.displayConfig?.gameConfig?.wheelSlices) && {
        wheelSlices: formData.displayConfig.gameConfig.wheelSlices
      })
    }
  };

  const quizQuestions = buildQuizQuestionsList(formData?.wofQuizQuestions);

  return {
    displayConfig,
    ...(hasAnyNonEmptyValue(formData?.translations)
      ? {translations: formData.translations}
      : {}),
    ...(quizQuestions.length > 0 ? {quizQuestions} : {})
  };
};

const buildVideoPublishPayload = (formData) => ({
  translations: formData?.translations ?? {},
  mediaUrls: formData?.mediaUrls ?? [],
  allTimeLimit: formData?.allTimeLimit,
  displayOrder:
    formData?.displayOrder !== '' &&
    formData?.displayOrder !== undefined &&
    formData?.displayOrder !== null
      ? formData.displayOrder
      : 0
});

/**
 * Activity segment fields — form vs API
 *
 * Redux / setup UI: `selectedSegments` (includes), `segmentExclude` (toggle), `excludedSegments` (chips)
 * — same field names as Rewards SegmentsCard expects. Requests must not send deprecated wire keys
 * `segments` or `excludeSegments`; use `includedSegmentGroups`, `excludedSegments`, `segmentExclude`.
 * Responses may still include older keys; `extractAndPreprocessData` maps them into form state.
 */

/**
 * Wire payload: `includedSegmentGroups` only (no flat `segments`).
 * Grouped UI passes through; a flat id list is wrapped as a single group (uses `buildIncludedSegmentGroups`).
 */
const buildActivitySegmentsPayload = (formData) => {
  const segs = formData?.selectedSegments ?? formData?.segments;
  if (!Array.isArray(segs)) return {};
  const isGrouped = segs.some(Array.isArray);
  const selectedForGroups = isGrouped
    ? segs
    : (() => {
        const flat = normalizeActivitySegmentInput(segs);
        return flat.length > 0 ? [flat] : [];
      })();
  if (selectedForGroups.length === 0) return {};
  const includedSegmentGroups = buildIncludedSegmentGroups(
    {selectedSegments: selectedForGroups},
    []
  );
  return includedSegmentGroups.length > 0 ? {includedSegmentGroups} : {};
};

/**
 * Map form `excludedSegments` chips (`{ label, value }` or names) to API `{ segmentId, segmentName }[]`.
 * Segments are keyed by name; the id comes from the chip or the optional segment list, else null.
 */
const buildActivityExcludedSegmentsForApi = (
  formData,
  segmentsCatalog = []
) => {
  if (!Array.isArray(formData?.excludedSegments)) return [];
  const idsByName = segmentIdsByName(segmentsCatalog);
  return formData.excludedSegments
    .map((s) => {
      const segmentName = segmentNameOf(s);
      const chipId = s != null && typeof s === 'object' ? s.segmentId : null;
      return {
        segmentId: chipId ?? idsByName.get(segmentName) ?? null,
        segmentName
      };
    })
    .filter((seg) => seg.segmentName);
};

/** Wire payload: `segmentExclude` and optional `excludedSegments`. */
const buildActivityExcludedPayload = (formData, segmentsCatalog = []) => {
  const out = {};
  const excludeOn = resolveRawSegmentExcludeFromForm(formData);
  if (excludeOn !== undefined && excludeOn !== null) {
    out.segmentExclude = !!excludeOn;
  }
  if (
    Array.isArray(formData?.excludedSegments) &&
    formData.excludedSegments.length > 0
  ) {
    const wired = buildActivityExcludedSegmentsForApi(
      formData,
      segmentsCatalog
    );
    if (wired.length > 0) {
      out.excludedSegments = wired;
    }
  }
  return out;
};

/**
 * Helper function to determine if an activity is being edited in published/in-progress state
 * For Point Expiration: checks for numeric ID (status may be null)
 * For other activities: checks status
 */
const checkIsEditingPublished = (activityFormData) => {
  if (activityFormData?.activityType === ACTIVITY_TYPE.POINT_EXPIRATION) {
    return !!(activityFormData?.id || activityFormData?.activityId);
  }
  return (
    isPublished(activityFormData?.status) ||
    isInProgress(activityFormData?.status)
  );
};

const getDisplayActivityType = (activityType) => {
  switch (activityType) {
    case ACTIVITY_TYPES_ENUM.WELCOME:
      return ACTIVITY_TYPE.WELCOME_ACTIVITY;
    case ACTIVITY_TYPES_ENUM.LOGIN:
      return ACTIVITY_TYPE.LOGIN;
    case ACTIVITY_TYPES_ENUM.GAME_FREE_KICK:
      return ACTIVITY_TYPE.GAME_FREE_KICK;
    case ACTIVITY_TYPES_ENUM.WATCH_VIDEO:
    case ACTIVITY_TYPE.WATCH_VIDEO:
      return ACTIVITY_TYPE.WATCH_VIDEO;
    case ACTIVITY_TYPES_ENUM.SURVEY:
      return ACTIVITY_TYPE.SURVEY;
    case ACTIVITY_TYPES_ENUM.POINTS_TRANSFER:
      return ACTIVITY_TYPE.POINTS_TRANSFER;
    case ACTIVITY_TYPES_ENUM.POINTS_ON_ORDER_DELIVERY:
      return ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY;
    case ACTIVITY_TYPES_ENUM.REDEEM_POINTS:
      return ACTIVITY_TYPE.REDEEM_POINTS;
    case ACTIVITY_TYPES_ENUM.GENERIC_ACTIVITY:
      return ACTIVITY_TYPE.GENERIC_ACTIVITY;
    case ACTIVITY_TYPES_ENUM.POINT_EXPIRATION:
      return ACTIVITY_TYPE.POINT_EXPIRATION;
    case ACTIVITY_TYPES_ENUM.PAY_BY_POINTS:
      return ACTIVITY_TYPE.PAY_BY_POINTS;
    case ACTIVITY_TYPES_ENUM.CURRENCY_TO_POINTS:
      return ACTIVITY_TYPE.CURRENCY_TO_POINTS;
    case ACTIVITY_TYPES_ENUM.GAME_WHEEL_OF_FORTUNE:
      return ACTIVITY_TYPE.GAME_WHEEL_OF_FORTUNE;
    case ACTIVITY_TYPES_ENUM.GAME_BACKGAMMON:
      return ACTIVITY_TYPE.GAME_BACKGAMMON;
    case ACTIVITY_TYPES_ENUM.EASY_ORDER:
      return ACTIVITY_TYPE.EASY_ORDER;
    default:
      return activityType;
  }
};

const extractAndPreprocessData = (formDataFromApi) => {
  const src = formDataFromApi?.data ?? formDataFromApi;
  const {
    activityType,
    pointsEarned,
    hasEndDate,
    dailyLoginLimit,
    allTimeLoginLimit,
    activityName,
    orderType,
    localCurrency,
    status,
    uuid,
    id,
    activityId,
    allCustomers,
    displayOrder: displayOrderRaw,
    customerType,
    url,
    mediaUrls,
    budgetPeriod,
    dailyDisplayStartTime,
    dailyDisplayEndTime,
    daysOfWeekUnavailable,
    dailyPlayLimit,
    allTimeLimit,
    displayConfig: srcDisplayConfig,
    translations,
    leaderboardRewardsList,
    quizQuestions
  } = src;

  const displayConfig = srcDisplayConfig ?? formDataFromApi?.displayConfig;

  const displayConfigFields = extractDisplayConfigFields(displayConfig, {
    dailyDisplayStartTime,
    dailyDisplayEndTime,
    daysOfWeekUnavailable,
    dailyPlayLimit,
    budgetPeriod,
    pointsEarned
  });

  const coerced = coerceNumericFields(src);

  const mappedStartDate = src?.activeStartDate || src?.activityStartDate;
  const mappedEndDate = src?.activeEndDate || src?.activityEndDate;

  const displayOrderFromApi =
    displayOrderRaw ?? src?.display_order ?? src?.displayOrder;
  const hasDisplayOrderFromApi =
    displayOrderFromApi !== undefined &&
    displayOrderFromApi !== null &&
    displayOrderFromApi !== '';

  return buildPreprocessedResult({
    activityType,
    pointsEarned: displayConfigFields.pointsEarned ?? coerced.pointsEarned,
    hasEndDate: mappedEndDate ? true : hasEndDate,
    startDate: mappedStartDate,
    endDate: mappedEndDate,
    dailyLoginLimit,
    allTimeLoginLimit:
      displayConfigFields.allTimeLoginLimit ??
      coerced.allTimeLoginLimit ??
      allTimeLoginLimit,
    activityName,
    orderType,
    localCurrency,
    numberOfCases: coerced.numberOfCases,
    noOfDays: coerced.noOfDays,
    receivePoints: coerced.receivePoints,
    status,
    uuid,
    id,
    activityId,
    selectedSegments: normalizeSegmentsFromApi(src),
    allCustomers,
    segmentExclude: resolveSegmentExcludeFromApi(src),
    excludedSegments: mapApiExcludedSegmentsToForm(src?.excludedSegments),
    pointsToRedeem: coerced.pointsToRedeem,
    localCurrencyValue: coerced.localCurrencyValue,
    displayOrder: resolveDisplayOrderFromApi({
      hasDisplayOrderFromApi,
      displayOrderFromApi
    }),
    customerType,
    url,
    translations,
    mediaUrls,
    budgetPeriod: displayConfigFields.budgetPeriod ?? budgetPeriod,
    dailyDisplayStartTime:
      displayConfigFields.dailyDisplayStartTime ?? dailyDisplayStartTime,
    dailyDisplayEndTime:
      displayConfigFields.dailyDisplayEndTime ?? dailyDisplayEndTime,
    daysOfWeekUnavailable:
      displayConfigFields.daysOfWeekUnavailable ?? daysOfWeekUnavailable,
    dailyPlayLimit: displayConfigFields.dailyPlayLimit ?? dailyPlayLimit,
    allTimeLimit: coerced.allTimeLimit ?? allTimeLimit,
    displayConfig,
    leaderboardRewardsList,
    quizQuestions,
    equalProbability: displayConfigFields.equalProbability,
    equalPoints: displayConfigFields.equalPoints,
    pointsForSuccessfulUpload: coerced.pointsForSuccessfulUpload,
    pointsPerLineItem: coerced.pointsPerLineItem,
    photoOrderModalDisplayLimit: coerced.photoOrderModalDisplayLimit
  });
};

const processDates = (startDate, endDate) => {
  const processedStartDate = getDateFromISOString(startDate);
  const processedEndDate = endDate ? getDateFromISOString(endDate) : endDate;
  return {processedStartDate, processedEndDate};
};

const createWelcomeActivityOrder = (processedData) => {
  const {
    activityType,
    pointsEarned,
    hasEndDate,
    processedStartDate: startDate,
    activityName,
    uuid,
    status,
    activityId,
    processedEndDate: endDate,
    selectedSegments,
    allCustomers
  } = processedData;

  return {
    activityType: activityType,
    pointsEarned: pointsEarned,
    hasEndDate: hasEndDate,
    startDate: startDate,
    ...(activityName && {activityName: activityName}),
    ...(uuid && {uuid: uuid}),
    ...(status && {status: status}),
    ...(activityId && {id: activityId}),
    ...(hasEndDate && endDate != null && endDate !== '' && {endDate: endDate}),
    ...(Array.isArray(selectedSegments) &&
      selectedSegments.length > 0 && {selectedSegments}),
    ...(allCustomers && {allCustomers}),
    ...excludedFieldsForReviewOrder(processedData)
  };
};

const createEasyOrderActivityOrder = (processedData) => {
  const {
    activityType,
    pointsForSuccessfulUpload,
    pointsPerLineItem,
    photoOrderModalDisplayLimit,
    hasEndDate,
    processedStartDate: startDate,
    activityName,
    uuid,
    status,
    activityId,
    processedEndDate: endDate
  } = processedData;

  return {
    activityType,
    pointsForSuccessfulUpload,
    pointsPerLineItem,
    photoOrderModalDisplayLimit,
    hasEndDate,
    startDate,
    ...(activityName && {activityName}),
    ...(uuid && {uuid}),
    ...(status && {status}),
    ...(activityId && {id: activityId}),
    ...(hasEndDate && endDate != null && endDate !== '' && {endDate}),
    ...excludedFieldsForReviewOrder(processedData)
  };
};

const createVideoActivityOrder = (processedData) => {
  const {
    activityType,
    pointsEarned,
    processedStartDate: startDate,
    processedEndDate: endDate,
    activityName,
    uuid,
    status,
    activityId,
    selectedSegments,
    allCustomers,
    allTimeLimit,
    displayOrder,
    translations,
    mediaUrls
  } = processedData;

  return {
    activityType,
    pointsEarned,
    startDate,
    endDate,
    displayOrder: String(
      displayOrder !== '' && displayOrder !== undefined && displayOrder !== null
        ? displayOrder
        : 0
    ),
    ...(activityName && {activityName}),
    ...(uuid && {uuid}),
    ...(status && {status}),
    ...(activityId && {id: activityId}),
    ...(allTimeLimit && {allTimeLimit: String(allTimeLimit)}),
    ...(Array.isArray(selectedSegments) &&
      selectedSegments.length > 0 && {selectedSegments}),
    ...(allCustomers !== undefined && {allCustomers}),
    ...excludedFieldsForReviewOrder(processedData),
    ...(translations && Object.keys(translations).length > 0 && {translations}),
    ...(Array.isArray(mediaUrls) && mediaUrls.length > 0 && {mediaUrls})
  };
};

const createSurveyActivityOrder = (processedData) => {
  const {
    activityType,
    pointsEarned,
    processedStartDate: startDate,
    processedEndDate: endDate,
    activityName,
    uuid,
    status,
    activityId,
    selectedSegments,
    allCustomers,
    allTimeLimit,
    displayOrder,
    customerType,
    url
  } = processedData;

  return {
    activityType,
    pointsEarned,
    startDate,
    endDate,
    displayOrder:
      displayOrder !== '' && displayOrder !== undefined && displayOrder !== null
        ? displayOrder
        : 0,
    ...(activityName && {activityName}),
    ...(uuid && {uuid}),
    ...(status && {status}),
    ...(activityId && {id: activityId}),
    ...(allTimeLimit && {allTimeLimit: String(allTimeLimit)}),
    ...(Array.isArray(selectedSegments) &&
      selectedSegments.length > 0 && {selectedSegments}),
    ...(allCustomers !== undefined && {allCustomers}),
    ...(Array.isArray(customerType) && customerType.length > 0
      ? {customerType}
      : {}),
    ...(url && {url}),
    ...excludedFieldsForReviewOrder(processedData)
  };
};

const createLoginActivityOrder = (processedData) => {
  const {
    activityType,
    pointsEarned,
    dailyLoginLimit,
    allTimeLoginLimit,
    processedStartDate: startDate,
    processedEndDate: endDate,
    receivePoints,
    activityName,
    uuid,
    status,
    activityId,
    selectedSegments,
    allCustomers
  } = processedData;

  return {
    activityType: activityType,
    pointsEarned: pointsEarned,
    ...(dailyLoginLimit && {dailyLoginLimit: dailyLoginLimit}),
    ...(allTimeLoginLimit && {allTimeLoginLimit: String(allTimeLoginLimit)}),
    startDate: startDate,
    endDate: endDate,
    receivePoints: receivePoints,
    ...(activityName && {activityName: activityName}),
    ...(uuid && {uuid: uuid}),
    ...(status && {status: status}),
    ...(activityId && {id: activityId}),
    ...(Array.isArray(selectedSegments) &&
      selectedSegments.length > 0 && {selectedSegments}),
    ...(allCustomers && {allCustomers}),
    ...excludedFieldsForReviewOrder(processedData)
  };
};

const createPointsOnOrderDeliveryActivityOrder = (
  processedData,
  countryCode
) => {
  const {
    activityType,
    activityName,
    orderType,
    uuid,
    status,
    activityId,
    processedStartDate: startDate,
    processedEndDate: endDate,
    numberOfCases,
    pointsEarned,
    localCurrency,
    selectedSegments,
    allCustomers
  } = processedData;

  const isCountryCodeFrequency = COUNTRY_CODES_FREQUENCY.includes(countryCode);

  const fieldsArranged = {
    activityType: activityType,
    activityName: activityName,
    orderType: orderType,
    uuid: uuid,
    status: status,
    id: activityId,
    startDate: startDate,
    endDate: endDate,
    hasEndDate: true,
    ...(Array.isArray(selectedSegments) &&
      selectedSegments.length > 0 && {selectedSegments}),
    ...(allCustomers && {allCustomers}),
    ...excludedFieldsForReviewOrder(processedData)
  };

  // Add frequency-based fields
  if (isCountryCodeFrequency) {
    fieldsArranged.numberOfCases = numberOfCases;
    fieldsArranged.pointsEarned = pointsEarned;
  } else {
    fieldsArranged.localCurrency = localCurrency;
    fieldsArranged.pointsEarned = pointsEarned;
  }

  // Filter out any undefined or null values
  return Object.entries(fieldsArranged).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

// Create Point Expiration activity order
const createPointExpirationActivityOrder = (processedData) => {
  const {
    activityType,
    noOfDays,
    uuid,
    status,
    id,
    activityId,
    selectedSegments,
    allCustomers
  } = processedData;

  // Use id if available, otherwise use activityId
  const numericId = id || activityId;

  return {
    activityType: activityType,
    noOfDays: noOfDays,
    ...(uuid && {uuid: uuid}),
    ...(status && {status: status}),
    ...(numericId && {id: numericId}),
    ...(Array.isArray(selectedSegments) &&
      selectedSegments.length > 0 && {selectedSegments}),
    ...(allCustomers && {allCustomers}),
    ...excludedFieldsForReviewOrder(processedData)
  };
};

const createPayByPointsActivityOrder = (processedData) => {
  const {
    activityType,
    activityName,
    pointsToRedeem,
    localCurrencyValue,
    uuid,
    status,
    activityId,
    processedStartDate: startDate,
    processedEndDate: endDate,
    hasEndDate,
    selectedSegments,
    allCustomers
  } = processedData;

  const fieldsArranged = {
    activityType: activityType,
    activityName: activityName,
    pointsToRedeem: pointsToRedeem,
    localCurrencyValue: localCurrencyValue,
    uuid: uuid,
    status: status,
    id: activityId,
    startDate: startDate,
    endDate: endDate,
    ...(hasEndDate != null && {hasEndDate}),
    ...(Array.isArray(selectedSegments) &&
      selectedSegments.length > 0 && {selectedSegments}),
    ...(allCustomers && {allCustomers}),
    ...excludedFieldsForReviewOrder(processedData)
  };

  return Object.entries(fieldsArranged).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

const createCurrencyToPointsActivityOrder = (processedData) => {
  const {
    activityType,
    activityName,
    localCurrency,
    pointsEarned,
    uuid,
    status,
    activityId,
    processedStartDate: startDate,
    processedEndDate: endDate,
    hasEndDate,
    selectedSegments,
    allCustomers
  } = processedData;

  // Extract numeric value from "10 sar" format
  const localCurrencyValue = localCurrency?.split(' ')[0] || localCurrency;

  const fieldsArranged = {
    activityType: activityType,
    ...(activityName && {activityName: activityName}),
    localCurrency: localCurrencyValue,
    pointsEarned: pointsEarned,
    uuid: uuid,
    status: status,
    id: activityId,
    startDate: startDate,
    ...(hasEndDate != null && {hasEndDate}),
    endDate: endDate,
    ...(Array.isArray(selectedSegments) &&
      selectedSegments.length > 0 && {selectedSegments}),
    ...(allCustomers && {allCustomers}),
    ...excludedFieldsForReviewOrder(processedData)
  };

  return Object.entries(fieldsArranged).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

const createFreeKickActivityOrder = (processedData) => {
  const {
    activityType,
    activityName,
    pointsEarned,
    processedStartDate: startDate,
    processedEndDate: endDate,
    uuid,
    status,
    activityId,
    selectedSegments,
    allCustomers,
    dailyDisplayStartTime,
    dailyDisplayEndTime,
    daysOfWeekUnavailable,
    dailyPlayLimit,
    displayConfig,
    translations,
    leaderboardRewardsList
  } = processedData;

  // Transform API format leaderboardRewardsList to UI format leaderboards
  const leaderboards = mapLeaderboardRewardsListToUI(leaderboardRewardsList);

  return {
    activityType,
    activityName,
    pointsEarned,
    startDate,
    endDate,
    ...(uuid && {uuid}),
    ...(status && {status}),
    ...(activityId && {id: activityId}),
    ...(dailyDisplayStartTime && {
      dailyDisplayStartTime: extractTimeFromISODateTime(dailyDisplayStartTime)
    }),
    ...(dailyDisplayEndTime && {
      dailyDisplayEndTime: extractTimeFromISODateTime(dailyDisplayEndTime)
    }),
    ...(Array.isArray(daysOfWeekUnavailable) &&
      daysOfWeekUnavailable.length > 0 && {daysOfWeekUnavailable}),
    ...(dailyPlayLimit != null && {dailyPlayLimit}),
    ...(displayConfig && {displayConfig}),
    ...(translations && {translations}),
    ...(Array.isArray(selectedSegments) &&
      selectedSegments.length > 0 && {selectedSegments}),
    ...(allCustomers !== undefined && {allCustomers}),
    ...excludedFieldsForReviewOrder(processedData),
    ...(Object.keys(leaderboards).length > 0 && {leaderboards})
  };
};

const createWheelOfFortuneActivityOrder = (processedData) => {
  const {
    activityType,
    pointsEarned,
    processedStartDate: startDate,
    processedEndDate: endDate
  } = processedData;

  return {
    activityType,
    pointsEarned,
    startDate,
    endDate,
    ...buildWofOptionalFields(processedData)
  };
};

export default function useActivityReview() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isCancel, setIsCancel] = useState(false);
  const activityFormData = useSelector((state) => state.activity.formData);
  const [isNameValid, setIsNameValid] = useState(true);

  const {
    showToast,
    toastMessage,
    handleActivityDurationValidation,
    handleToastClose,
    resetValidation,
    handleViewActivity,
    handlePointExpirationPublishError
  } = useActivityValidation();

  const handleResetFormData = () => {
    const defaultFormData = {
      activityType: '',
      hasEndDate: false
    };
    dispatch(resetActivityData(defaultFormData));
  };

  const countryCode = useAppSelector((state) => state.countryCode).countryCode;

  const getActivityName = () => {
    const {activityType, uuid, activityName} = activityFormData || {};

    if (activityType === ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY) {
      return activityName;
    }

    if (activityType === ACTIVITY_TYPE.SURVEY) {
      return resolveTrimmedName(activityName, 'SURVEY');
    }

    if (isVideoActivityType(activityType)) {
      return resolveTrimmedName(activityName, 'WATCH_VIDEO');
    }

    if (isFreeKickActivityType(activityType)) {
      return resolveTrimmedName(activityName, 'GAME_FREE_KICK');
    }

    if (isBackgammonActivityType(activityType)) {
      return resolveTrimmedName(activityName, 'GAME_BACKGAMMON');
    }

    if (isWheelOfFortuneActivityType(activityType)) {
      return resolveTrimmedName(activityName, 'GAME_WHEEL_OF_FORTUNE');
    }

    if (activityType === ACTIVITY_TYPE.PAY_BY_POINTS) {
      return activityName || `PAY_BY_POINTS_${formatUTCTimestamp()}`;
    }

    if (activityType === ACTIVITY_TYPE.CURRENCY_TO_POINTS) {
      return activityName || `CURRENCY_TO_POINTS_${formatUTCTimestamp()}`;
    }

    if (activityType === ACTIVITY_TYPE.EASY_ORDER) {
      return resolveTrimmedName(activityName, 'EASY_ORDER');
    }

    const shouldUseTimestampName =
      !uuid || (typeOfActivity.includes(activityType) && !activityName);

    if (shouldUseTimestampName) {
      return `${getActivityEnum(activityType)}_${formatUTCTimestamp()}`;
    }

    return activityName;
  };

  const validateName = async (name) => {
    const activityType = activityFormData?.activityType;
    if (!requiresNameValidation(activityType)) {
      setIsNameValid(true);
      return true;
    }

    if (!name || String(name).trim() === '') {
      dispatch(
        setToastNotifications({
          show: true,
          message: `${getNameValidationLabel(activityType)} is mandatory`,
          type: 'error'
        })
      );
      return false;
    }

    const params = {
      id: activityFormData?.id ?? null,
      uuid: activityFormData?.uuid ?? null,
      name
    };
    const resp = await handleNameValidationViaCallbackFn(
      validateActivityName,
      params
    );
    const isValid = resp?.success === true;
    setIsNameValid(isValid);
    if (!isValid) {
      dispatch(
        setToastNotifications({
          show: true,
          message:
            "It looks like there's already an Activity with this name. Please enter a unique name before continuing.",
          type: 'error'
        })
      );
    }
    return isValid;
  };

  const isPointExpirationDuplicateError = (errorMessage) => {
    if (activityFormData?.activityType !== ACTIVITY_TYPE.POINT_EXPIRATION) {
      return false;
    }
    const message = (errorMessage || '').toLowerCase();
    return (
      message.includes('points expiry') || message.includes('already exists')
    );
  };

  const fetchExistingPointExpirationActivity = async () => {
    const response = await handleGetActivity(
      `activityType=${ACTIVITY_TYPES_ENUM.POINT_EXPIRATION}`,
      false
    );
    return (
      response?.data?.content?.[0]?.activityId ||
      response?.data?.content?.[0]?.id
    );
  };

  const handlePointExpirationDuplicateError = async () => {
    try {
      const existingActivityId = await fetchExistingPointExpirationActivity();
      handlePointExpirationPublishError(existingActivityId);
    } catch {
      handlePointExpirationPublishError(null);
    }
  };

  const prepareDates = () => {
    const activityStartDateIso = getISOTimeStringForReview(
      activityFormData?.startDate,
      false,
      true
    );
    const activityEndDateIso = getISOTimeStringForReview(
      activityFormData?.endDate,
      true,
      false
    );
    const shouldSendEndDate =
      activityFormData?.activityType === ACTIVITY_TYPE.WELCOME_ACTIVITY ||
      activityFormData?.activityType === ACTIVITY_TYPE.PAY_BY_POINTS ||
      activityFormData?.activityType === ACTIVITY_TYPE.EASY_ORDER
        ? !!activityFormData?.hasEndDate
        : true;

    return {
      activityStartDateValue: activityStartDateIso ?? '',
      activityEndDateValue: shouldSendEndDate ? (activityEndDateIso ?? '') : ''
    };
  };

  const validatePublishDuration = async (
    uniqueActivityName,
    isEditingPublished
  ) => {
    let validationParams = {
      ...activityFormData,
      activityName: uniqueActivityName
    };
    if (isEditingPublished) {
      validationParams.id =
        activityFormData?.id || activityFormData?.activityId;
    }
    return await handleActivityDurationValidation(
      omitDeprecatedSegmentWireKeys(validationParams)
    );
  };

  const buildPublishParams = (
    uniqueActivityName,
    dates,
    isEditingPublished
  ) => {
    const activityType = activityFormData?.activityType;
    const isFreeKick =
      activityType === ACTIVITY_TYPE.GAME_FREE_KICK ||
      isBackgammonActivityType(activityType);
    const isEasyOrder = activityType === ACTIVITY_TYPE.EASY_ORDER;

    const base = {
      title: '',
      activityType: getActivityEnum(activityType),
      tags: getTags(activityType),
      slug: getSlug(activityType, activityFormData?.slug),
      ...(isWheelOfFortuneActivityType(activityType) || isEasyOrder
        ? {}
        : {pointsEarned: getPointsEarnedForPublish(activityFormData)}),
      ...(!isFreeKick &&
        !isEasyOrder && {
          maxLimitPerUser: 0,
          dailyLoginLimit: activityFormData?.dailyLoginLimit,
          conversionRate: 0,
          amountPerUnit: 0,
          pointsAwardedPerUnit: 0,
          allTimeLoginLimit: activityFormData?.allTimeLoginLimit
        }),
      activityStartDate: dates.activityStartDateValue,
      activityEndDate: dates.activityEndDateValue,
      activityName: uniqueActivityName,
      ...(!isEditingPublished && {
        uuid: activityFormData?.uuid || getUUID(uniqueActivityName)
      }),
      orderType: activityFormData?.orderType,
      localCurrency: getFormattedLocalCurrency(activityFormData, countryCode),
      ...(COUNTRY_CODES_FREQUENCY.includes(countryCode)
        ? {numberOfCases: activityFormData?.numberOfCases}
        : {}),
      hasEndDate: activityFormData?.hasEndDate,
      receivePoints: activityFormData?.receivePoints,
      noOfDays: activityFormData?.noOfDays,
      status: ACTIVITY_STATUS.PUBLISHED,
      ...(isEditingPublished && {activityName: activityFormData.activityName}),
      ...(isEditingPublished && {
        id: activityFormData.id || activityFormData.activityId
      }),
      allCustomers: activityFormData?.allCustomers,
      ...buildActivitySegmentsPayload(activityFormData),
      ...buildActivityExcludedPayload(activityFormData)
    };

    const typeSpecific = {};
    if (activityType === ACTIVITY_TYPE.SURVEY) {
      Object.assign(typeSpecific, buildSurveyPublishPayload(activityFormData));
    }
    if (isVideoActivityType(activityType)) {
      Object.assign(typeSpecific, buildVideoPublishPayload(activityFormData));
    }
    if (
      activityType === ACTIVITY_TYPE.GAME_FREE_KICK ||
      isBackgammonActivityType(activityType)
    ) {
      Object.assign(
        typeSpecific,
        buildFreeKickGamePublishPayload(activityFormData)
      );
    }
    if (isWheelOfFortuneActivityType(activityType)) {
      Object.assign(
        typeSpecific,
        buildWheelOfFortunePublishPayload(activityFormData)
      );
    }
    if (isEasyOrder) {
      Object.assign(
        typeSpecific,
        buildEasyOrderPublishPayload(activityFormData)
      );
    }

    return {...base, ...typeSpecific};
  };

  const filterPublishParams = (param, dates) => {
    const manuallyIncludedKeys = [
      'tags',
      'applicationId',
      'slug',
      'activityName',
      'uuid',
      'status',
      'id',
      'includedSegmentGroups',
      'allCustomers',
      'segmentExclude',
      'excludedSegments',
      'pointsEarned',
      'localCurrency',
      'hasEndDate',
      'activityStartDate',
      'activityEndDate',
      'displayOrder',
      'allTimeLimit',
      'dailyDisplayStartTime',
      'dailyDisplayEndTime',
      'daysOfWeekUnavailable',
      'dailyPlayLimit',
      'gameConfig',
      'displayConfig',
      'leaderboardRewardsList',
      'translations',
      'quizQuestions',
      'pointsForSuccessfulUpload',
      'pointsPerLineItem',
      'photoOrderModalDisplayLimit'
    ];

    const isOptionalEndDateActivity =
      activityFormData?.activityType === ACTIVITY_TYPE.WELCOME_ACTIVITY ||
      activityFormData?.activityType === ACTIVITY_TYPE.PAY_BY_POINTS ||
      activityFormData?.activityType === ACTIVITY_TYPE.EASY_ORDER;

    return Object.fromEntries(
      Object.entries(param).filter(([key, value]) => {
        if (
          key === 'activityEndDate' &&
          isOptionalEndDateActivity &&
          !activityFormData?.hasEndDate
        ) {
          return false;
        }

        if (manuallyIncludedKeys.includes(key)) {
          return true;
        }

        const isFromFormData =
          key in activityFormData ||
          value === getActivityEnum(activityFormData?.activityType) ||
          value ===
            getSlug(activityFormData?.activityType, activityFormData?.slug) ||
          value === dates.activityStartDateValue ||
          value === dates.activityEndDateValue ||
          value === activityFormData?.orderType;

        return (
          isFromFormData &&
          value !== undefined &&
          value !== null &&
          value !== ''
        );
      })
    );
  };

  const handlePublishSuccess = (isEditingPublished) => {
    dispatch(setActivityFormData(activityFormData));
    dispatch(
      setToastNotifications({
        show: true,
        message: isEditingPublished
          ? 'Activity updated successfully'
          : 'New Activity published',
        type: 'success'
      })
    );
    resetValidation();
    handleResetFormData();
    navigate(ROUTES.ACTIVITIES);
  };

  const handlePublishError = async (error, isResponse = false) => {
    const errorMessage = isResponse
      ? error?.message
      : error?.cause || error?.message || '';

    if (isPointExpirationDuplicateError(errorMessage)) {
      if (!isResponse) {
        dispatch(
          setToastNotifications({show: false, message: '', type: 'error'})
        );
      }
      await handlePointExpirationDuplicateError();
      return;
    }

    const errMsg = isResponse
      ? error?.errorMessage || error?.message || 'An unknown error occurred'
      : error?.cause ||
        error?.response?.data?.errorMessage ||
        error?.response?.data?.message ||
        error?.message ||
        'An unknown error occurred';

    dispatch(
      setToastNotifications({show: true, message: errMsg, type: 'error'})
    );
  };

  const handlePublish = async (options = {}) => {
    const {onSuccess} = options;
    if (!activityFormData?.activityType) {
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Activity Type is mandatory',
          type: 'error'
        })
      );
      return;
    }

    const uniqueActivityName = getActivityName();
    const isCurrencyToPoints =
      activityFormData?.activityType === ACTIVITY_TYPE.CURRENCY_TO_POINTS;

    // Skip name validation for Currency to Points (auto-generated name)
    if (activityFormData?.status == null && !isCurrencyToPoints) {
      const isValidName = await validateName(uniqueActivityName);
      if (!isValidName) return;
    }

    const dates = prepareDates();
    const isEditingPublished = checkIsEditingPublished(activityFormData);

    // Skip duration validation for Currency to Points (API doesn't support it yet)
    if (!isCurrencyToPoints) {
      const isDurationValid = await validatePublishDuration(
        uniqueActivityName,
        isEditingPublished
      );
      if (!isDurationValid) return;
    }

    const param = buildPublishParams(
      uniqueActivityName,
      dates,
      isEditingPublished
    );

    const formBasedParams = omitDeprecatedSegmentWireKeys(
      filterPublishParams(param, dates)
    );

    try {
      const response = isEditingPublished
        ? await handleUpdateActivity({data: formBasedParams})
        : await handleCreateActivity({data: formBasedParams});

      if (response?.success) {
        if (onSuccess) {
          dispatch(
            setActivityFormData({...activityFormData, status: 'PUBLISHED'})
          );
          onSuccess(response);
        } else {
          handlePublishSuccess(isEditingPublished);
        }
      } else {
        await handlePublishError(response, true);
      }
    } catch (error) {
      await handlePublishError(error, false);
    }
  };

  const buildBaseDraftData = (uniqueActivityName, dates) => {
    const activityType = activityFormData?.activityType;

    // FreeKick uses explicit payload building to avoid spreading entire form state
    if (
      isFreeKickActivityType(activityType) ||
      isBackgammonActivityType(activityType)
    ) {
      return buildFreeKickDraftPayload({
        ...activityFormData,
        activityName: uniqueActivityName,
        activityStartDate: dates.activityStartDateValue,
        activityEndDate: dates.activityEndDateValue,
        activityType: getActivityEnum(activityType),
        status: ACTIVITY_STATUS.DRAFT,
        uuid: activityFormData?.uuid || getUUID(uniqueActivityName)
      });
    }

    if (activityType === ACTIVITY_TYPE.EASY_ORDER) {
      return buildEasyOrderDraftPayload({
        ...activityFormData,
        activityName: uniqueActivityName,
        activityStartDate: dates.activityStartDateValue,
        activityEndDate: dates.activityEndDateValue,
        activityType: getActivityEnum(activityType),
        status: ACTIVITY_STATUS.DRAFT,
        uuid: activityFormData?.uuid || getUUID(uniqueActivityName)
      });
    }

    let draft = {
      ...activityFormData,
      activityName: uniqueActivityName,
      activityStartDate: dates.activityStartDateValue,
      activityEndDate: dates.activityEndDateValue,
      activityType: getActivityEnum(activityFormData?.activityType),
      status: ACTIVITY_STATUS.DRAFT,
      uuid: activityFormData?.uuid || getUUID(uniqueActivityName),
      ...buildActivitySegmentsPayload(activityFormData),
      ...buildActivityExcludedPayload(activityFormData)
    };
    if (isWheelOfFortuneActivityType(activityType)) {
      draft = buildWheelOfFortuneDraft(activityFormData, draft);
    }
    return omitDeprecatedSegmentWireKeys(draft);
  };

  const transformPayByPointsData = (draftData) => {
    draftData.tags = 'paid-by-points';
    draftData.slug = getSlug(
      activityFormData?.activityType,
      activityFormData?.slug
    );
    draftData.pointsEarned = activityFormData?.pointsToRedeem;
    draftData.localCurrency = activityFormData?.localCurrencyValue;
    delete draftData.pointsToRedeem;
    delete draftData.localCurrencyValue;
  };

  const saveDraft = async (draftData) => {
    return activityFormData?.uuid
      ? await handleUpdateDraftActivity({data: draftData})
      : await handleSaveDraftActivity({data: draftData});
  };

  const handleDraftResponse = (
    response,
    {redirectOnSuccess = true} = {},
    draftUuid = null
  ) => {
    let message = 'Failed to save draft';
    if (response?.success) {
      if (activityFormData?.uuid) {
        message = 'Activity draft updated';
      } else {
        message = 'New Activity draft';
      }
    }
    const type = response?.success ? 'success' : 'error';

    dispatch(setToastNotifications({show: true, message, type}));

    if (response?.success) {
      if (redirectOnSuccess) {
        handleResetFormData();
        navigate(ROUTES.ACTIVITIES);
      } else {
        // Update Redux formData with the saved activity id/uuid so
        // child components (e.g. WOF Attributes) can reference it.
        // Do NOT use extractAndPreprocessData here: the draft-save
        // response is minimal and would overwrite activityType etc.
        const responseData = response?.data ?? response;
        const mergedId =
          responseData?.id || responseData?.activityId || activityFormData?.id;
        const mergedActivityId =
          responseData?.activityId ||
          responseData?.id ||
          activityFormData?.activityId;
        dispatch(
          setActivityFormData({
            ...activityFormData,
            id: mergedId,
            activityId: mergedActivityId,
            uuid: draftUuid || activityFormData?.uuid
          })
        );
      }
    }
  };

  const handleDraft = async ({redirectOnSuccess = true} = {}) => {
    if (!activityFormData?.activityType) {
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Activity Type is mandatory',
          type: 'error'
        })
      );
      return;
    }

    const activityType = activityFormData?.activityType;
    const isNamelessActivity =
      activityType === ACTIVITY_TYPE.WELCOME_ACTIVITY ||
      activityType === ACTIVITY_TYPE.LOGIN;

    const isRequiredActivityNameMissing =
      !isNamelessActivity &&
      (!activityFormData?.activityName ||
        String(activityFormData.activityName).trim() === '');

    if (isRequiredActivityNameMissing) {
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Activity Name is mandatory',
          type: 'error'
        })
      );
      return;
    }

    const uniqueActivityName = getActivityName();
    const isValidName = await validateName(uniqueActivityName);
    if (!isValidName) return;

    const dates = prepareDates();
    const baseDraftData = buildBaseDraftData(uniqueActivityName, dates);

    if (activityFormData?.activityType === ACTIVITY_TYPE.PAY_BY_POINTS) {
      transformPayByPointsData(baseDraftData);
    }

    const response = await saveDraft(baseDraftData);
    handleDraftResponse(response, {redirectOnSuccess}, baseDraftData.uuid);
  };

  const handleCancel = () => {
    setIsCancel((v) => !v);
  };

  const onClickPrimaryButton = () => {
    handleResetFormData();
    navigate(ROUTES.ACTIVITIES);
  };

  const onClickSecondaryButton = () => {
    setIsCancel((v) => !v);
  };

  const handleValidationModalClose = () => {
    setIsNameValid(true);
  };

  const getFormDataWithOrder = useCallback(
    (formDataFromApi) => {
      // Extract and preprocess data
      const extractedData = extractAndPreprocessData(formDataFromApi);

      // Process dates
      const {processedStartDate, processedEndDate} = processDates(
        extractedData.startDate,
        extractedData.endDate
      );

      // Get display activity type
      const displayActivityType = getDisplayActivityType(
        extractedData.activityType
      );

      // Combine all processed data
      const processedData = {
        ...extractedData,
        activityType: displayActivityType,
        processedStartDate,
        processedEndDate
      };

      // Return appropriate order based on activity type
      if (displayActivityType === ACTIVITY_TYPE.WELCOME_ACTIVITY) {
        return createWelcomeActivityOrder(processedData);
      } else if (displayActivityType === ACTIVITY_TYPE.LOGIN) {
        return createLoginActivityOrder(processedData);
      } else if (
        displayActivityType === ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY
      ) {
        return createPointsOnOrderDeliveryActivityOrder(
          processedData,
          countryCode
        );
      } else if (displayActivityType === ACTIVITY_TYPE.POINT_EXPIRATION) {
        return createPointExpirationActivityOrder(processedData);
      } else if (displayActivityType === ACTIVITY_TYPE.PAY_BY_POINTS) {
        return createPayByPointsActivityOrder(processedData);
      } else if (displayActivityType === ACTIVITY_TYPE.SURVEY) {
        return createSurveyActivityOrder(processedData);
      } else if (isVideoActivityType(displayActivityType)) {
        return createVideoActivityOrder(processedData);
      } else if (displayActivityType === ACTIVITY_TYPE.GAME_FREE_KICK) {
        return createFreeKickActivityOrder(processedData);
      } else if (displayActivityType === ACTIVITY_TYPE.GAME_BACKGAMMON) {
        return createFreeKickActivityOrder(processedData);
      } else if (displayActivityType === ACTIVITY_TYPE.CURRENCY_TO_POINTS) {
        return createCurrencyToPointsActivityOrder(processedData);
      } else if (displayActivityType === ACTIVITY_TYPE.EASY_ORDER) {
        return createEasyOrderActivityOrder(processedData);
      } else if (isWheelOfFortuneActivityType(displayActivityType)) {
        return createWheelOfFortuneActivityOrder(processedData);
      } else {
        return {};
      }
    },
    [countryCode]
  );

  return {
    handlePublish,
    handleDraft,
    handleCancel,
    onClickPrimaryButton,
    onClickSecondaryButton,
    isCancel,
    activityFormData,
    getFormDataWithOrder,
    isNameValid,
    handleValidationModalClose,
    validateName,
    getActivityName,
    showToast,
    toastMessage,
    handleToastClose,
    handleViewActivity,
    handleActivityDurationValidation,
    resetValidation
  };
}
